export type AtmosphereQuality = "low" | "medium" | "high";

export interface AtmosphereFeatureFlags {
  readonly lighting: boolean;
  readonly fog: boolean;
  readonly grading: boolean;
  readonly particles: boolean;
}

export const DEFAULT_ATMOSPHERE_FLAGS: AtmosphereFeatureFlags = Object.freeze({
  lighting: true,
  fog: true,
  grading: true,
  particles: true,
});

export const ATMOSPHERE_CONFIG = Object.freeze({
  sunDirection: Object.freeze([-0.48, 0.78, 0.39] as const),
  ambientColor: Object.freeze([0.58, 0.61, 0.63] as const),
  sunColor: Object.freeze([0.54, 0.51, 0.46] as const),
  fogColor: Object.freeze([0.295, 0.305, 0.298] as const),
  fogNear: 102,
  fogFar: 138,
  fogMaximum: 0.92,
  maximumParticles: 64,
  particleCounts: Object.freeze({ low: 24, medium: 48, high: 64 }),
});

export interface AtmosphereFogProfile {
  readonly fogColor: readonly [number, number, number];
  readonly fogNear: number;
  readonly fogFar: number;
  readonly fogMaximum: number;
}

export interface BiomeAtmosphereProfile extends AtmosphereFogProfile {
  readonly ambientColor: readonly [number, number, number];
  readonly particleRatio: number;
}

export const TPS_ATMOSPHERE_CONFIG = Object.freeze({
  fogNear: 40,
  // The 2 x 64 m terrain window leaves at least 128 m around the player.
  // Finish the radial fade early enough to allow the TPS camera boom and lag.
  fogFar: 108,
  fogMaximum: 1,
  skyZenithColor: Object.freeze([0.16, 0.185, 0.18] as const),
});

export function tpsFogProfile(biome: AtmosphereFogProfile): AtmosphereFogProfile {
  return {
    fogColor: biome.fogColor,
    fogNear: TPS_ATMOSPHERE_CONFIG.fogNear,
    fogFar: TPS_ATMOSPHERE_CONFIG.fogFar,
    fogMaximum: TPS_ATMOSPHERE_CONFIG.fogMaximum,
  };
}

// Sharing the ray-dependent color makes fully faded terrain/objects match the
// sky even when distant hills rise above the mathematical horizon.
export const TPS_SKY_COLOR_GLSL = `
uniform float uDynamicEnvironment;
uniform vec3 uSkyZenithColor;
uniform vec3 uSkySunDirection;
uniform vec3 uSkySunColor;
uniform float uCloudCoverage;
uniform vec2 uCloudOffset;
float cloudHash(vec2 p) { p = mod(p, 4096.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float cloudNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(cloudHash(i), cloudHash(i + vec2(1.0, 0.0)), f.x), mix(cloudHash(i + vec2(0.0, 1.0)), cloudHash(i + vec2(1.0)), f.x), f.y);
}
vec3 tpsSkyColor(vec3 direction) {
  float elevation = smoothstep(0.03, 0.72, direction.y);
  if (uDynamicEnvironment < 0.5) return mix(uFogColor, vec3(${TPS_ATMOSPHERE_CONFIG.skyZenithColor.join(", ")}), elevation);
  vec3 sky = mix(uFogColor, uSkyZenithColor, elevation);
  float solarDot = max(0.0, dot(direction, normalize(uSkySunDirection)));
  sky += uSkySunColor * pow(solarDot, 18.0) * 0.2;
  sky += uSkySunColor * smoothstep(0.9995, 0.99985, solarDot) * 2.2;
  if (uCloudCoverage > 0.01 && direction.y > 0.006) {
    vec2 p = direction.xz / max(0.14, direction.y + 0.14) * 2.8 + uCloudOffset;
    float noise = cloudNoise(p) * 0.57 + cloudNoise(p * 2.0) * 0.28 + cloudNoise(p * 4.0) * 0.15;
    float cover = smoothstep(1.0 - uCloudCoverage * 0.92, 1.15 - uCloudCoverage * 0.85, noise);
    cover *= smoothstep(0.006, 0.12, direction.y);
    vec3 clouds = mix(uFogColor * 0.58, uFogColor * 1.25, noise) + uSkySunColor * pow(solarDot, 8.0) * 0.16;
    sky = mix(sky, clouds, cover * 0.93);
  }
  return sky;
}`;

export const ENVIRONMENT_SKY_UNIFORM_NAMES = ["uDynamicEnvironment", "uSkyZenithColor", "uSkySunDirection", "uSkySunColor", "uCloudCoverage", "uCloudOffset"] as const;

export const BIOME_ATMOSPHERE_PROFILES: Readonly<Record<BiomeId, BiomeAtmosphereProfile>> = Object.freeze({
  ASHLANDS: Object.freeze({ fogColor: [0.295, 0.305, 0.298] as const, fogNear: 128, fogFar: 185, fogMaximum: 0.82, ambientColor: [0.57, 0.6, 0.62] as const, particleRatio: 1 }),
  GROUND_ZERO: Object.freeze({ fogColor: [0.32, 0.31, 0.29] as const, fogNear: 145, fogFar: 215, fogMaximum: 0.78, ambientColor: [0.6, 0.6, 0.59] as const, particleRatio: 0.62 }),
  FERTILE_PRESERVE: Object.freeze({ fogColor: [0.34, 0.38, 0.32] as const, fogNear: 140, fogFar: 205, fogMaximum: 0.74, ambientColor: [0.6, 0.64, 0.57] as const, particleRatio: 0 }),
  NUCLEAR_FOREST: Object.freeze({ fogColor: [0.19, 0.23, 0.18] as const, fogNear: 105, fogFar: 150, fogMaximum: 0.9, ambientColor: [0.46, 0.51, 0.47] as const, particleRatio: 0.18 }),
  RUINED_CIVILIZATION: Object.freeze({ fogColor: [0.3, 0.31, 0.31] as const, fogNear: 130, fogFar: 190, fogMaximum: 0.8, ambientColor: [0.58, 0.6, 0.61] as const, particleRatio: 0.48 }),
  INDUSTRIAL_RUINS: Object.freeze({ fogColor: [0.28, 0.29, 0.29] as const, fogNear: 128, fogFar: 188, fogMaximum: 0.82, ambientColor: [0.55, 0.57, 0.58] as const, particleRatio: 0.62 }),
});
export function blendBiomeAtmosphere(
  primary: BiomeId,
  secondary: BiomeId | null,
  secondaryWeight: number,
): BiomeAtmosphereProfile {
  const first = BIOME_ATMOSPHERE_PROFILES[primary];
  if (!secondary || secondaryWeight <= 0) return first;
  const second = BIOME_ATMOSPHERE_PROFILES[secondary];
  const amount = Math.max(0, Math.min(0.5, secondaryWeight));
  const mix = (a: number, b: number): number => a + (b - a) * amount;
  return {
    fogColor: [mix(first.fogColor[0], second.fogColor[0]), mix(first.fogColor[1], second.fogColor[1]), mix(first.fogColor[2], second.fogColor[2])],
    fogNear: mix(first.fogNear, second.fogNear),
    fogFar: mix(first.fogFar, second.fogFar),
    fogMaximum: mix(first.fogMaximum, second.fogMaximum),
    ambientColor: [mix(first.ambientColor[0], second.ambientColor[0]), mix(first.ambientColor[1], second.ambientColor[1]), mix(first.ambientColor[2], second.ambientColor[2])],
    particleRatio: mix(first.particleRatio, second.particleRatio),
  };
}

export function particleCountForQuality(quality: AtmosphereQuality): number {
  return ATMOSPHERE_CONFIG.particleCounts[quality];
}

export function createAtmosphereParticleData(count: number = ATMOSPHERE_CONFIG.maximumParticles): Float32Array {
  const data = new Float32Array(count * 4);
  let state = 0x6d2b79f5;
  const random = (): number => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
  for (let index = 0; index < count; index += 1) {
    const offset = index * 4;
    data[offset] = (random() - 0.5) * 56;
    data[offset + 1] = 0.7 + random() * 8.2;
    data[offset + 2] = (random() - 0.5) * 56;
    data[offset + 3] = random();
  }
  return data;
}
import type { BiomeId } from "@endclock/shared";
